/**
 * 分段控件 / 标签栏的滑动滑块。
 *
 * 为什么是 translate 而不是动 width：
 * 动 width 会触发布局重排，每帧都要重新计算，是掉帧的头号来源。
 * 这里让滑块固定占 `100 / count %` 的宽度，只改 xPercent，
 * 既拿到同样的视觉效果，又全程停在合成层。
 *
 * 组件侧只需要：容器 position relative，滑块宽度 `100/count%`，
 * 其余交给这个 hook。
 */

import { useEffect, useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

export function useSlidingPill<T extends HTMLElement = HTMLDivElement>(
	index: number,
	options: { springBack?: boolean } = {},
): RefObject<T> {
	const ref = useRef<T | null>(null);
	const first = useRef(true);

	useEffect(() => {
		const pill = ref.current;
		if (!pill) return;

		const xPercent = index * 100;

		// 首次直接到位，不要从第 0 个滑过来
		if (first.current || prefersReducedMotion()) {
			first.current = false;
			gsap.set(pill, { xPercent });
			return;
		}

		gsap.to(pill, {
			xPercent,
			duration: options.springBack === false ? MOTION.flip : 0.42,
			ease: MOTION.ease.out,
			overwrite: 'auto',
		});
	}, [index, options.springBack]);

	return ref as RefObject<T>;
}
